import * as React from 'react'
import { cn } from '@/lib/utils'
import TablePaginate from '@/components/ui/table-paginate'

export interface ModuleTableColumn<T> {
	key: React.Key
	header: React.ReactNode
	className?: string
	render?: (row: T, rowIndex: number) => React.ReactNode
	accessor?: (row: T) => React.ReactNode
}

export interface ModuleTablePaginationProps {
	page: number
	pageSize: number
	total: number
	onPageChange: (page: number) => void
	readOnlyPageDisplay?: boolean
	hideOnSinglePage?: boolean
	disabled?: boolean
}

export interface ModuleTableProps<T> {
	title: string
	columns: ModuleTableColumn<T>[]
	data: T[]
	totalItems?: number
	headerAction?: React.ReactNode
	emptyState?: React.ReactNode
	pagination?: ModuleTablePaginationProps
	className?: string
	tableClassName?: string
	footerExtra?: React.ReactNode
	getRowKey?: (row: T, index: number) => React.Key
	ariaLabel?: string
}

export function ModuleTable<T>(props: ModuleTableProps<T>) {
	const {
		title,
		columns,
		data,
		totalItems,
		headerAction,
		emptyState,
		pagination,
		className,
		tableClassName,
		footerExtra,
		getRowKey,
		ariaLabel,
	} = props

	const total = totalItems ?? data.length
	const totalPages = pagination
		? Math.max(1, Math.ceil(total / pagination.pageSize))
		: 1

	return (
		<section
			className={cn(
				'rounded-lg shadow-sm overflow-hidden',
				className
			)}
			style={{
				background:
					'linear-gradient(360deg, rgba(0, 87, 184, 0) 50%, rgba(0, 87, 184, 0.09) 100%), linear-gradient(0deg, #FFFFFF, #FFFFFF)',
				border: '2px solid transparent',
				borderImageSource:
					'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0) 100%)',
				borderImageSlice: 1,
			}}
			aria-label={ariaLabel || title}
		>
			<div className='flex items-start justify-between gap-4 px-5 pt-4 pb-3'>
				<h2 className='text-base font-medium text-[#0A0D12]'>{title}</h2>
				{headerAction && <div className='shrink-0'>{headerAction}</div>}
			</div>
			<div className='overflow-x-auto'>
				<table
					className={cn(
						'w-full border-separate border-spacing-0 text-left text-sm',
						tableClassName
					)}
				>
					<thead>
						<tr className='text-xs font-medium text-[#475467]'>
							{columns.map((col) => (
								<th
									key={col.key}
									scope='col'
									className={cn(
										'h-9 px-5 align-middle font-medium first:pl-5 last:pr-5',
										'border-b border-[#EAECF0] bg-transparent text-[#475467]',
										col.className
									)}
								>
									{col.header}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{data.length === 0 ? (
							<tr>
								<td
									colSpan={columns.length}
									className='px-5 py-10 text-center text-sm text-[#667085]'
								>
									{emptyState || 'No data available'}
								</td>
							</tr>
						) : (
							data.map((row, rowIndex) => {
								const key = getRowKey
									? getRowKey(row, rowIndex)
									: (row as any)?.id ?? rowIndex
								return (
									<tr
										key={key}
										className={cn(
											'group text-[#0A0D12] hover:bg-[#F5F9FC] transition-colors',
											'border-b last:border-b-0 border-[#EAECF0]'
										)}
									>
										{columns.map((col) => (
											<td
												key={col.key}
												className={cn(
													'h-11 px-5 py-2 align-middle text-sm leading-snug first:pl-5 last:pr-5',
													col.className
												)}
											>
												{col.render
													? col.render(row, rowIndex)
													: col.accessor
													? col.accessor(row)
													:
														(row as any)?.[col.key as any] ?? null}
											</td>
										))}
									</tr>
								)
							})
						)}
					</tbody>
				</table>
			</div>
			{(footerExtra || pagination) && (
				<div className='flex flex-col gap-3 border-t border-[#EAECF0] bg-white px-5 py-4 md:flex-row md:items-center md:justify-between'>
					{footerExtra && <div className='text-sm text-[#475467]'>{footerExtra}</div>}
					{pagination && (
						<TablePaginate
							page={pagination.page}
							totalPages={totalPages}
							onPageChange={pagination.onPageChange}
							readOnlyPageDisplay={pagination.readOnlyPageDisplay}
							hideOnSinglePage={pagination.hideOnSinglePage}
							disabled={pagination.disabled}
							className='ml-auto'
						/>
					)}
				</div>
			)}
		</section>
	)
}

export default ModuleTable
